/**
 * Why a shop hour could not be priced.
 *
 * Mirrors `SHOP_RATE_GAP_MESSAGES` in the API's `src/services/shop-pay.service.ts`.
 * Same relationship `work-locations.ts` has to the `timesheets_work_location_check`
 * constraint: a copy with no database behind it, so it can drift silently, and
 * the test beside it is what stops that.
 *
 * ── THE RESPONSE ALREADY CARRIES PROSE, AND THAT IS WHAT SHOULD RENDER ──────
 * Every unpriced row arrives with a non-null `gapMessage` written by the API.
 * Rendering that, rather than a second wording maintained here, is the point —
 * two answers to one question is how the two drift and nobody knows later which
 * one is right.
 *
 * The map below is therefore the FALLBACK ONLY: it covers a row whose
 * `gapMessage` did not arrive, which is the key-drift failure that renders a
 * cell as nothing. It is keyed on every reason the API can emit, because a map
 * that guesses or echoes a raw enum value is how a display bug gets reported as
 * a data bug.
 *
 * ── NULL IS NOT ZERO, ONE LAYER EARLIER ─────────────────────────────────────
 * `hourlyRate` and `amount` are `number | null`, and the API returns null with a
 * reason rather than reaching for the worker's `hourly` or `other` rate — both of
 * which are a percentage OF A LINE ITEM on most tenants, and a shop shift has no
 * line item. A stored rate of 0 also resolves to `rate_has_no_hourly_amount`
 * rather than to $0.00. Nothing here may turn any of that back into a number.
 */

export const SHOP_RATE_GAPS = [
  /**
   * This worker is deliberately not paid from jobs (`users.job_pay_exemption`
   * is set). NOT a missing rate, and the only reason in this list that nobody
   * should act on — see `JOB_PAY_EXEMPT_GAP` below.
   */
  'job_pay_exempt',
  /** No `employee_pay_rates` row with line_item_type='shop' for this worker. */
  'no_shop_rate_configured',
  /** A row exists but its effective window does not cover the day worked. */
  'rate_not_effective_on_date',
  /** A row covers the day but carries no usable hourly amount. */
  'rate_has_no_hourly_amount',
  /**
   * An HOURLY-basis worker (US tenants, api#1097) with no pay profile at all.
   * Their rate lives on the pay profile, not on a Shop pay-rate row — so the
   * remedy is a different screen. For such a worker the API also re-words
   * `rate_not_effective_on_date` and `rate_has_no_hourly_amount` to name the
   * pay profile; that wording arrives in the server's message, which wins.
   */
  'no_hourly_rate_configured',
] as const;

export type ShopRateGap = (typeof SHOP_RATE_GAPS)[number];

/**
 * The one reason in `SHOP_RATE_GAPS` that is not a gap.
 *
 * Every other value means "somebody should configure something". This one means
 * "somebody already decided, and the decision was no". Rendering it in the same
 * warning colour as the other three is what made the shop tab's gap count
 * un-actionable in the first place, so callers branch on this rather than
 * treating every non-null `gap` alike.
 *
 * Named as a constant because it is tested against in three places and a typo'd
 * string literal in any of them fails silently — the row would simply render as
 * a warning again, which is exactly the bug this closes and looks like nothing.
 */
export const JOB_PAY_EXEMPT_GAP = 'job_pay_exempt' as const;

/** Whether a row's `gap` is the deliberate-exemption one rather than a real gap. */
export function isJobPayExemptGap(gap: unknown): boolean {
  return gap === JOB_PAY_EXEMPT_GAP;
}

/**
 * Fallback wording, one entry per reason. Kept close to the API's own text so a
 * reader cannot tell which layer produced the sentence they are looking at.
 */
export const SHOP_RATE_GAP_FALLBACK_MESSAGES: Record<ShopRateGap, string> = {
  job_pay_exempt:
    'This worker is not paid from jobs, so their shop hours are recorded but not '
    + 'priced. Nothing is missing. Change this on their employee profile if it is wrong.',
  no_shop_rate_configured:
    'No shop rate is configured for this worker. Add a "Shop" row on their pay '
    + 'rates with an hourly amount. Their hours are recorded and will price once it exists.',
  rate_not_effective_on_date:
    'This worker has a shop rate, but its effective dates do not cover the day they worked. '
    + 'Check the effective-from and effective-to dates on the Shop row.',
  rate_has_no_hourly_amount:
    'This worker has a Shop pay-rate row, but no hourly amount on it. Shop time is paid per '
    + 'hour, so the percentage columns on that row cannot price it.',
  no_hourly_rate_configured:
    'This worker is paid hourly but has no hourly rate. Set their pay profile to Hourly with a '
    + 'base rate. Their hours are recorded and will price once it exists.',
};

export function isShopRateGap(value: unknown): value is ShopRateGap {
  return typeof value === 'string' && (SHOP_RATE_GAPS as readonly string[]).includes(value);
}

/**
 * The sentence to show beside an unpriced row.
 *
 * Order is deliberate: the server's own wording wins, the local map covers a
 * dropped `gapMessage`, and a reason this build has never heard of still gets a
 * sentence saying the hours are unpriced. It NEVER returns '' — an unpriced row
 * with nothing beside it is the silent-blank-field bug, and it is worse here than
 * elsewhere because the cell it sits next to is where money would have been.
 */
export function shopGapMessage(gap: unknown, serverMessage: unknown): string {
  if (typeof serverMessage === 'string' && serverMessage.trim() !== '') {
    return serverMessage.trim();
  }
  if (isShopRateGap(gap)) return SHOP_RATE_GAP_FALLBACK_MESSAGES[gap];
  return 'These hours are not priced, and the reason was not reported. Check this '
    + "worker's Shop pay rate.";
}

/**
 * The approve route's pay verdict — `shop_pay` on POST /timesheets/:id/approve.
 * Same keys for a shop shift and, since api#1097, a US hourly worker's FIELD
 * shift: nothing in it is hourly-only, and `hourlyRate` is then the worker's
 * pay-profile base rate.
 */
export interface ShopPayFeedResult {
  filed: boolean;
  created: boolean;
  timesheetEntryId: string | null;
  payPeriodId: string | null;
  carriedForward: boolean;
  hours: number | null;
  hourlyRate: number | null;
  overtimeEligible: boolean | null;
  amount: number | null;
  gap: string | null;
  message: string | null;
}

/**
 * Feed reasons that are not something the office should act on after an
 * approve:
 *   not_shop_work  — a field shift of a ticket-paid worker; the ticket pays it.
 *   not_approved   — cannot follow a successful approve.
 *   job_pay_exempt — a decision somebody already made (JOB_PAY_EXEMPT_GAP).
 */
const QUIET_FEED_GAPS = new Set<string>(['not_shop_work', 'not_approved', JOB_PAY_EXEMPT_GAP]);

/**
 * The sentence to show after an approve that went through but filed no pay,
 * or null when there is nothing to say.
 *
 * Before api#1097 this screen read nothing of `shop_pay`, which was survivable
 * while only shop shifts were priced here. It stops being survivable for a US
 * hourly hand: their FIELD shift is now their pay, and an approve that cannot
 * price it (`no_hourly_rate_configured`) is the only moment the office learns
 * the worker has no rate. A green "approved" over that is the reassuring wrong
 * answer. The approve itself stood, so this is a warning, never an error.
 *
 * Absent `shop_pay` (null / undefined, a pre-#1097 response) says nothing —
 * there is no verdict to report, and inventing one would be a guess.
 */
export function approvalPayNotice(shopPay: unknown): string | null {
  if (!shopPay || typeof shopPay !== 'object') return null;
  const sp = shopPay as Partial<ShopPayFeedResult>;
  if (sp.filed === true) return null;
  if (typeof sp.gap !== 'string' || sp.gap === '' || QUIET_FEED_GAPS.has(sp.gap)) return null;
  return shopGapMessage(sp.gap, sp.message);
}
